import { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { FileText, BookOpen, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { apiFetch } from '../../../shared/lib/api';

export default function AIAssistantSection() {
  const [inputText, setInputText] = useState('');
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'summary' | 'explain'>('summary');

  const handleAIAction = async (type: 'summary' | 'explain') => {
    if (!inputText.trim()) return;
    setLoading(true);
    setResult('');
    setActiveTab(type);

    try {
      const response = await apiFetch(`/api/ai/${type}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: inputText }),
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      setResult(data.result);
    } catch (error: any) {
      setResult(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="ai-assistant" className="py-24">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-4">AI Accounting Assistant</h2>
          <p className="text-slate-600 dark:text-slate-400">Paste your accounting text or concepts below to get started.</p>
        </div>

        <div className="glass-card p-6 md:p-8">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Input Text</label>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="e.g., Paste a balance sheet summary or ask 'What is EBITDA?'"
                className="w-full h-48 p-4 rounded-xl border border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all resize-none bg-slate-50/50 dark:bg-slate-800/50 text-slate-900 dark:text-white"
              />
            </div>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => handleAIAction('summary')}
                disabled={loading || !inputText.trim()}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                {loading && activeTab === 'summary' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                Summarize Text
              </button>
              <button
                onClick={() => handleAIAction('explain')}
                disabled={loading || !inputText.trim()}
                className="btn-secondary flex-1 flex items-center justify-center gap-2"
              >
                {loading && activeTab === 'explain' ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
                Explain Concept
              </button>
            </div>

            <AnimatePresence mode="wait">
              {(loading || result) && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-8 p-6 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700"
                >
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                      AI Result: {activeTab === 'summary' ? 'Summary' : 'Explanation'}
                    </span>
                    {loading && <Loader2 className="w-4 h-4 text-primary animate-spin" />}
                  </div>
                  
                  {loading ? (
                    <div className="space-y-3">
                      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded animate-pulse w-3/4" />
                      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded animate-pulse w-full" />
                      <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded animate-pulse w-5/6" />
                    </div>
                  ) : (
                    <div className="markdown-body">
                      <ReactMarkdown>{result}</ReactMarkdown>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
